import { useEffect, useState } from 'react'

const openStateCache = new Map<string, boolean>()

function readStoredOpen(key: string): boolean | null {
  try {
    const stored = window.localStorage.getItem(key)
    if (stored === 'true') return true
    if (stored === 'false') return false
  } catch {
    // Ignore quota / privacy mode failures.
  }
  return null
}

function writeStoredOpen(key: string, open: boolean) {
  openStateCache.set(key, open)
  try {
    window.localStorage.setItem(key, String(open))
  } catch {
    // Ignore quota / privacy mode failures.
  }
}

/**
 * Persists Collapsible open state across Inertia remounts (module cache)
 * and full page reloads (localStorage). SSR/hydration always starts from
 * `fallbackOpen` to avoid text/DOM mismatches.
 */
export function usePersistedCollapsibleOpen(storageKey: string, fallbackOpen: boolean) {
  const [open, setOpen] = useState(() => openStateCache.get(storageKey) ?? fallbackOpen)

  useEffect(() => {
    if (openStateCache.has(storageKey)) {
      setOpen(openStateCache.get(storageKey)!)
      return
    }

    const stored = readStoredOpen(storageKey)
    if (stored !== null) {
      openStateCache.set(storageKey, stored)
      setOpen(stored)
      return
    }

    openStateCache.set(storageKey, fallbackOpen)
  }, [fallbackOpen, storageKey])

  const onOpenChange = (next: boolean) => {
    writeStoredOpen(storageKey, next)
    setOpen(next)
  }

  return [open, onOpenChange] as const
}
